/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Returns true when `hostname` is an RFC-1918 private IPv4 address.
 * Covers all three private ranges:
 *   10.0.0.0/8       — starts with "10."
 *   172.16.0.0/12    — starts with "172." and second octet 16–31
 *   192.168.0.0/16   — starts with "192.168."
 *
 * The previous check (host.startsWith("192")) was a brittle heuristic that
 * missed the 10.x and 172.16–31.x ranges entirely and would misroute any
 * public host whose name happens to begin with "192" (Item 17 fix).
 */
export function isPrivateHost(hostname: string): boolean {
  if (hostname.startsWith("10.")) return true;
  if (hostname.startsWith("192.168.")) return true;
  if (hostname.startsWith("172.")) {
    // Extract the second octet and check it is in range 16–31
    const parts = hostname.split(".");
    const second = parseInt(parts[1], 10);
    return second >= 16 && second <= 31;
  }
  return false;
}

export const configuration = {
  // Getter defers window.location access to call time rather than module init.
  // This prevents ReferenceError in SSR / Node.js test environments where
  // window may not be available when the module is first imported.
  get backendURL(): string {
    /* c8 ignore next 4 */
    // SSR guard: window is always defined in jsdom and in the browser;
    // this branch only fires in true server-side environments.
    if (typeof window === "undefined") {
      return (
        import.meta.env.VITE_REACT_APP_BACKEND_URL ?? ""
      ); /* c8 ignore next */
    }
    const hostname = new URL(window.location.href).hostname;
    // Item 17 fix: use isPrivateHost() to detect all RFC-1918 ranges, not just
    // the 192.x prefix. See isPrivateHost() above for the full range coverage.
    return isPrivateHost(hostname)
      ? import.meta.env.VITE_REACT_APP_SECONDARY_BACKEND_URL
      : import.meta.env.VITE_REACT_APP_BACKEND_URL;
  },
  get shoppingCartUrl(): string {
    const raw = import.meta.env.VITE_REACT_APP_SHOPPING_CART_URL;
    /* c8 ignore next 9 */
    // Vite inlines import.meta.env values at transform time, so this branch
    // is unreachable in Vitest — the env var is always substituted by the
    // vitest.config.ts `env` block. Verified in production: the app fails
    // fast at call time if VITE_REACT_APP_SHOPPING_CART_URL is omitted from
    // the deploy environment.
    if (!raw) {
      throw new Error(
        "VITE_REACT_APP_SHOPPING_CART_URL is not set. " +
          "It must be an absolute origin, e.g. https://shop.deskshare.com — see .env.example.",
      );
    }
    return raw;
  },
  // BUG-3: Public website URL — used by "Explore Products" on the login page.
  // Opens the marketing site where products are listed without requiring login,
  // instead of the shop dashboard (which bounces unauthenticated visitors).
  // Configurable so it can point at a specific catalog page later without a
  // code change.
  get websiteUrl(): string {
    const raw = import.meta.env.VITE_REACT_APP_WEBSITE_URL;
    /* c8 ignore next 2 */
    // Same Vite transform-time inlining: this branch is unreachable in Vitest.
    if (!raw) throw new Error("VITE_REACT_APP_WEBSITE_URL is not set.");
    return raw;
  },

  /** Feedback Management System base URL — website reviews only.
   *  Direct fetch bypasses apiClient (which always prefixes backendURL)
   *  so reviews reach the feedback service while other forms use the main backend.
   */
  get feedbackAPIURL(): string {
    return (import.meta.env.VITE_FEEDBACK_API_URL ?? "").replace(/\/$/, "");
  },
};

/**
 * Build a proper media URL from a path.
 * Handles both full URLs (http/https) and relative paths.
 * Adds /media/ prefix for relative paths if needed.
 * Local static assets (starting with /assets/ or /static/) are returned
 * as-is.
 *
 * Item 12 (Session 361): /images/ was in this list and is deliberately not any
 * more. That path is owned by the older ASP.NET site, so returning it
 * unchanged sent a broken address straight out to the browser. With nothing
 * this application owns under that prefix, keeping the branch would only
 * preserve the ability to make the same mistake again — anything still
 * arriving under /images/ is a remote path and is treated as one.
 */
export const getMediaUrl = (path: string | null | undefined): string => {
  if (!path) return "";

  // Already a full URL
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // Local static assets - return as-is
  if (path.startsWith("/assets/") || path.startsWith("/static/")) {
    return path;
  }

  // Already has /media/ prefix
  if (path.startsWith("/media/")) {
    return `${configuration.backendURL}${path}`;
  }

  // Relative path - add /media/ prefix
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${configuration.backendURL}/media/${cleanPath}`;
};

/**
 * Build a video stream URL from a backend stream path.
 * Always prepends backendURL — stream paths are never standalone URLs.
 * Use this for all video streaming endpoints so URL changes require
 * a single update point rather than scattered inline concatenation.
 */
export const getVideoStreamUrl = (
  streamPath: string | null | undefined,
): string => {
  if (!streamPath) return "";
  if (streamPath.startsWith("http://") || streamPath.startsWith("https://")) {
    return streamPath;
  }
  const cleanPath = streamPath.startsWith("/") ? streamPath : `/${streamPath}`;
  return `${configuration.backendURL}${cleanPath}`;
};

/**
 * Resolve a feature video URL, preferring the stream endpoint over the
 * media file path. Falls back to getMediaUrl when no stream URL is present.
 * Centralises URL logic for Features.tsx, Video.tsx, and VideoTutorialsAndManuals.tsx.
 */
export const getFeatureVideoStreamUrl = (
  streamUrl: string | null | undefined,
  mediaPath: string | null | undefined,
): string => {
  if (streamUrl) {
    return getVideoStreamUrl(streamUrl);
  }
  return getMediaUrl(mediaPath);
};
